import * as THREE from 'three';
import { SPHERE_CONTROLLER_LOADER } from '../Controllers/SphereController';

export class VRSphere {
  constructor({ scene, camera, model, rot, background }) {
    this.model = model;
    this.scene = scene;
    this.camera = camera;
    this.url = `${String(background.mediumPath).split('.')[0]}`;

    const radius = 1200;
    const segmentsX = 16;
    const segmentsY = 8;

    this.sphereSegments = [];
    this.sphereGroup = new THREE.Group();
    this.tileMaterials = {};  // Initialize tileMaterials as a property of the VRSphere instance
    this.scene.add(this.sphereGroup);

    for (let i = 0; i < segmentsX; i++) {
      for (let j = 0; j < segmentsY; j++) {
        const segment = this.createSphereSegment(radius, segmentsX - i - 1, j, segmentsX, segmentsY);
        this.sphereSegments.push(segment);
        this.sphereGroup.add(segment.mesh);
        segment.mesh.visible = false;
      }
    }

    this.sphereGroup.rotation.y = rot + Math.PI;
  }

  createSphereSegment(radius, i, j, segmentsX, segmentsY) {
    const phiStart = i * Math.PI * 2 / segmentsX;
    const phiLength = Math.PI * 2 / segmentsX;
    const thetaStart = j * Math.PI / segmentsY;
    const thetaLength = Math.PI / segmentsY;

    const geometry = new THREE.SphereGeometry(radius, 32, 32, phiStart, phiLength, thetaStart, thetaLength);
    geometry.scale(-1, 1, 1);
    const material = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide, transparent: false });
    const mesh = new THREE.Mesh(geometry, material);
    return { mesh, material, loaded: false, i, j, segmentsX, segmentsY };
  }

  updateSphereSegments() {
    const frustum = new THREE.Frustum();
    const cameraViewProjectionMatrix = new THREE.Matrix4();
    this.camera.updateMatrixWorld();
    this.camera.matrixWorldInverse.copy(this.camera.matrixWorld).invert();
    cameraViewProjectionMatrix.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse);
    frustum.setFromProjectionMatrix(cameraViewProjectionMatrix);
    for (const segment of this.sphereSegments) {
      if (frustum.intersectsObject(segment.mesh)) {
        if (!segment.loaded) {
          this.loadTile(segment);
          segment.loaded = true;
        }
      }
    }
  }

  loadTile(segment) {
    const name = `tile_${segment.i}_${segment.j}.jpg`;

    // Check if the texture for this tile already exists in the cache
    if (this.tileMaterials[name]) {
      segment.mesh.material.map = this.tileMaterials[name];
      segment.mesh.material.needsUpdate = true;
      segment.mesh.visible = true;
      return;
    }
    const loader = new THREE.TextureLoader();
    const texturePath = `${this.url}/tile_${segment.i}_${segment.j}.jpg`;
    loader.load(texturePath, (texture) => {
      texture.wrapS = THREE.ClampToEdgeWrapping;
      texture.wrapT = THREE.ClampToEdgeWrapping;
      segment.mesh.material.map = texture;
      segment.mesh.material.needsUpdate = true;

      // Cache the loaded texture in tileMaterials for the specific scene
      this.tileMaterials[name] = texture;
      segment.mesh.visible = true;
    }, null, () => {
      // Dispatch an error event if the texture fails to load
      this.model.dispatchEvent({ type: SPHERE_CONTROLLER_LOADER.ERROR_TILE_LOADING });
    });
  }

  setInitRotation(rot) {
    this.sphereGroup.rotation.y = rot + Math.PI;
  }

  startSceneChange(background) {
    for (const segment of this.sphereSegments) {
      segment.mesh.visible = false;
      segment.loaded = false;
    }
  }

  stopSceneChanged() {
    const rot = this.model.editMode ? 0 : (this.model.currentScene.sceneRotationY || 0);
    this.sphereGroup.rotation.y = Math.PI + rot;
    this.updateSphereSegments();
    setTimeout(() => {
      this.updateSphereSegments();
    }, 50);
  }
}
